import { arrayMove } from '@dnd-kit/sortable';
import { Check, GripVertical, Plus, Trash2, X } from 'lucide-react';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { get, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { Attribute, Product } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@nucleus-commerce/ui/components/command';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaIdField } from '@/features/media';

import { optionValueCode } from '../api/mappers';
import {
  dictionaryValue,
  optionValueLabel,
  optionValueStoredCode,
} from '../api/option-sync-preview';
import { useSortableOption } from '../hooks/use-sortable-option';
import type {
  OptionAxisForm,
  OptionValueForm,
  ProductFormInput,
} from '../schemas/product-form-schema';

import { SortableOptionList } from './option-sortable';

const COLOR = /^#[0-9a-fA-F]{6}$/;

type SavedAxis = Product['options'][number];

/** One id per value within its option, stable across a reorder. */
const valueId = (value: OptionValueForm): string =>
  value.attributeValueId ?? value.value ?? value.label ?? '';

function valueColor(attribute: Attribute | undefined, value: OptionValueForm): string | null {
  if (value.swatch === null) return null;
  if (value.swatch?.color) return value.swatch.color;
  return value.swatch === undefined
    ? (dictionaryValue(attribute, value)?.swatch?.color ?? null)
    : null;
}

function ValueDot({ color, className }: { color: string | null; className?: string }) {
  return color ? (
    <span
      className={cn('border-border inline-block size-2.5 shrink-0 rounded-full border', className)}
      style={{ background: color }}
      aria-hidden
    />
  ) : null;
}

const chipClass = (fresh: boolean) =>
  cn(
    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[13px]',
    fresh ? 'border-primary/50 bg-primary-tint text-primary font-medium' : 'bg-background',
  );

/** A value shown on a store's page, where the option only reads. */
export function StaticValueChip({
  attribute,
  value,
}: {
  attribute: Attribute | undefined;
  value: OptionValueForm;
}) {
  return (
    <li className={chipClass(false)}>
      <ValueDot color={valueColor(attribute, value)} />
      {optionValueLabel(attribute, value)}
    </li>
  );
}

function SortableChip({
  attribute,
  value,
  index,
  count,
  fresh,
  onMove,
}: {
  attribute: Attribute | undefined;
  value: OptionValueForm;
  index: number;
  count: number;
  /** Not on the saved product yet. */
  fresh: boolean;
  onMove: (from: number, to: number) => void;
}) {
  const { t } = useTranslation('products');
  const label = optionValueLabel(attribute, value);
  const { setNodeRef, style, isDragging, handle } = useSortableOption({
    id: valueId(value),
    index,
    count,
    axis: 'x',
    onMove,
  });
  return (
    <li ref={setNodeRef} style={style} className={cn(isDragging && 'z-10 opacity-40')}>
      <span
        {...handle}
        aria-label={t('optionsCard.reorder', { name: label })}
        title={t('optionsCard.dragChip')}
        className={cn(
          chipClass(fresh),
          'focus-visible:ring-ring/50 cursor-grab touch-none outline-none focus-visible:ring-[3px] active:cursor-grabbing',
        )}
      >
        <ValueDot color={valueColor(attribute, value)} />
        {label}
      </span>
    </li>
  );
}

/** Add another value: the dictionary's for a select attribute, typed for any other. */
function AddValue({
  attribute,
  chosen,
  optionName,
  onAdd,
}: {
  attribute: Attribute;
  chosen: readonly OptionValueForm[];
  optionName: string;
  onAdd: (value: OptionValueForm) => void;
}) {
  const { t } = useTranslation('products');
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  if (attribute.dataType === 'select') {
    const chosenIds = new Set(chosen.map((c) => c.attributeValueId));
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="border-primary/50 text-primary hover:bg-primary-tint inline-flex w-full items-center gap-1 rounded-lg border border-dashed px-2.5 py-1.5 text-left text-[13px]"
          >
            <Plus className="size-3.5" aria-hidden />
            {t('optionsCard.addValue')}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <Command>
            <CommandInput placeholder={t('optionsCard.searchValues')} />
            <CommandList>
              <CommandEmpty>{t('optionsCard.noValues')}</CommandEmpty>
              <CommandGroup>
                {attribute.values.map((v) => {
                  const selected = chosenIds.has(v.id);
                  return (
                    <CommandItem
                      key={v.id}
                      value={v.label}
                      disabled={selected}
                      onSelect={() => onAdd({ attributeValueId: v.id, label: '' })}
                    >
                      <ValueDot color={v.swatch?.color ?? null} />
                      <span className="flex-1">{v.label}</span>
                      {selected ? <Check className="size-4" aria-hidden /> : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  const commit = () => {
    const label = draft.trim();
    if (!label) return;
    const code = optionValueCode(label);
    if (!code) {
      setProblem(t('optionsCard.valueNeedsLetters'));
      return;
    }
    if (chosen.some((c) => c.value === code)) {
      setProblem(t('optionsCard.valueTaken'));
      return;
    }
    setProblem(null);
    setDraft('');
    onAdd({ value: code, label });
  };

  return (
    <div className="flex flex-col gap-1">
      <Input
        value={draft}
        aria-label={t('optionsCard.addValueTo', { option: optionName })}
        aria-invalid={problem !== null}
        placeholder={
          attribute.unit
            ? t('optionsCard.typeValueUnit', { unit: attribute.unit })
            : t('optionsCard.typeValue')
        }
        className="border-primary/50 h-8 border-dashed text-[13px]"
        onChange={(e) => {
          setDraft(e.target.value);
          setProblem(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        // Typed and left is meant to count, and the save bar only shows once it does.
        onBlur={commit}
      />
      {problem ? <p className="text-destructive text-xs">{problem}</p> : null}
    </div>
  );
}

/** Shopper label, swatch colour and swatch image of one value. */
function ValuePanel({
  attribute,
  value,
  error,
  onChange,
}: {
  attribute: Attribute | undefined;
  value: OptionValueForm;
  error: string | undefined;
  onChange: (value: OptionValueForm) => void;
}) {
  const { t } = useTranslation('products');
  const id = useId();
  const dictionary = dictionaryValue(attribute, value);
  const label = optionValueLabel(attribute, value);
  const swatchColor = value.swatch?.color ?? '';
  const swatchMediaId = value.swatch?.mediaId ?? null;
  const usesDictionarySwatch = value.swatch === undefined && dictionary !== undefined;
  const colorInvalid = swatchColor !== '' && !COLOR.test(swatchColor);

  const setSwatch = (patch: { color?: string; mediaId?: string | null }) => {
    const nextColor = patch.color ?? swatchColor;
    const nextMediaId = patch.mediaId === undefined ? swatchMediaId : patch.mediaId;
    onChange({
      ...value,
      swatch:
        !nextColor && !nextMediaId
          ? null
          : {
              ...(nextColor ? { color: nextColor } : {}),
              ...(nextMediaId ? { mediaId: nextMediaId } : {}),
            },
    });
  };

  return (
    <div className="bg-background my-1 ml-6 grid gap-3 rounded-lg border p-3 sm:grid-cols-3">
      <div className="grid content-start gap-1.5">
        <Label htmlFor={`${id}-label`} className="text-xs">
          {t('optionsCard.shopperLabel')}
        </Label>
        <Input
          id={`${id}-label`}
          value={value.label ?? ''}
          placeholder={dictionary?.label ?? value.value ?? ''}
          maxLength={120}
          className="h-8 text-[13px]"
          onChange={(e) => onChange({ ...value, label: e.target.value })}
        />
        {dictionary ? (
          <p className="text-muted-foreground text-xs">
            {t('optionsCard.shopperLabelHint', { label: dictionary.label })}
          </p>
        ) : null}
      </div>
      <div className="grid content-start gap-1.5">
        <Label htmlFor={`${id}-color`} className="text-xs">
          {t('optionsCard.swatchColor')}
        </Label>
        <Input
          id={`${id}-color`}
          value={swatchColor}
          placeholder={usesDictionarySwatch ? (dictionary.swatch?.color ?? '#F3D27A') : '#F3D27A'}
          className="h-8 font-mono text-[13px]"
          aria-invalid={colorInvalid}
          onChange={(e) => setSwatch({ color: e.target.value.trim() })}
        />
        {dictionary && !usesDictionarySwatch ? (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className="justify-start"
            onClick={() => onChange({ ...value, swatch: undefined })}
          >
            {t('optionsCard.useDictionarySwatch')}
          </Button>
        ) : null}
        {colorInvalid ? (
          <p className="text-destructive text-xs">{t('optionsCard.swatchColorFormat')}</p>
        ) : null}
      </div>
      <div className="grid content-start gap-1.5">
        <span className="text-xs font-medium">{t('optionsCard.swatchImage')}</span>
        <MediaIdField
          value={swatchMediaId}
          onChange={(mediaId) => setSwatch({ mediaId })}
          label={t('optionsCard.swatchImageFor', { value: label })}
        />
      </div>
      {error && !colorInvalid ? (
        <p className="text-destructive text-xs sm:col-span-3">{error}</p>
      ) : null}
    </div>
  );
}

function ValueRow({
  attribute,
  value,
  index,
  count,
  fresh,
  error,
  panelOpen,
  onTogglePanel,
  onChange,
  onMove,
  onRemove,
}: {
  attribute: Attribute | undefined;
  value: OptionValueForm;
  index: number;
  count: number;
  fresh: boolean;
  error: string | undefined;
  panelOpen: boolean;
  onTogglePanel: () => void;
  onChange: (value: OptionValueForm) => void;
  onMove: (from: number, to: number) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('products');
  const label = optionValueLabel(attribute, value);
  const color = valueColor(attribute, value);
  const { setNodeRef, style, isDragging, handle } = useSortableOption({
    id: valueId(value),
    index,
    count,
    axis: 'y',
    onMove,
  });
  return (
    <li ref={setNodeRef} style={style} className={cn(isDragging && 'relative z-10 opacity-40')}>
      <div className="flex items-center gap-2 py-0.5">
        <button
          type="button"
          {...handle}
          aria-label={t('optionsCard.reorder', { name: label })}
          title={t('optionsCard.dragRow')}
          className="text-muted-foreground focus-visible:ring-ring/50 shrink-0 cursor-grab touch-none rounded outline-none focus-visible:ring-[3px] active:cursor-grabbing"
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
        <button
          type="button"
          aria-label={t('optionsCard.swatchFor', { value: label })}
          aria-expanded={panelOpen}
          title={t('optionsCard.swatchTitle')}
          onClick={onTogglePanel}
          className={cn(
            'size-5.5 shrink-0 rounded-full',
            color ? 'border-border border' : 'border-muted-foreground/60 border border-dashed',
          )}
          style={color ? { background: color } : undefined}
        />
        <span
          className={cn(
            'min-w-0 flex-1 truncate rounded-lg border px-2.5 py-1 text-[13px]',
            fresh ? 'border-primary/50' : 'bg-background',
            error && 'border-destructive',
          )}
        >
          {label}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          aria-label={t('optionsCard.removeValue', { value: label })}
          onClick={onRemove}
        >
          <Trash2 aria-hidden />
        </Button>
      </div>
      {panelOpen ? (
        <ValuePanel attribute={attribute} value={value} error={error} onChange={onChange} />
      ) : null}
    </li>
  );
}

export interface OptionRowProps {
  axis: OptionAxisForm;
  index: number;
  count: number;
  attribute: Attribute | undefined;
  /** This option on the saved product, to mark what's new. Undefined on the create page. */
  savedAxis: SavedAxis | undefined;
  /** This option's values as the form was seeded, so one added back is the saved value again. */
  savedValues?: readonly OptionValueForm[] | undefined;
  /** Compare against the saved product; false on the create page, where everything is new. */
  markNew: boolean;
  open: boolean;
  errors: FieldErrors<ProductFormInput>;
  onOpen: () => void;
  onClose: () => void;
  onChange: (axis: OptionAxisForm) => void;
  onMove: (from: number, to: number) => void;
  onValueMoved: (name: string, position: number, count: number) => void;
  onRemove: () => void;
}

/** One option of the card: a closed row of chips, or, after Edit, its fields in place. */
export function OptionRow({
  axis,
  index,
  count,
  attribute,
  savedAxis,
  savedValues,
  markNew,
  open,
  errors,
  onOpen,
  onClose,
  onChange,
  onMove,
  onValueMoved,
  onRemove,
}: OptionRowProps) {
  const { t } = useTranslation('products');
  const nameId = useId();
  const [panelFor, setPanelFor] = useState<string | null>(null);
  const attributeLabel = attribute?.label ?? axis.attributeId;
  const title = axis.name.trim() || attributeLabel;
  const { setNodeRef, style, isDragging, handle } = useSortableOption({
    id: axis.attributeId,
    index,
    count,
    axis: 'y',
    onMove,
  });

  // Done hands focus back to the Edit button that took it away.
  const editButton = useRef<HTMLButtonElement>(null);
  const refocusEdit = useRef(false);
  useLayoutEffect(() => {
    if (!refocusEdit.current) return;
    refocusEdit.current = false;
    editButton.current?.focus();
  });

  const message = (path: string) => get(errors, `options.${index}.${path}`) as string | undefined;
  const nameError = message('name.message');
  const rowError =
    message('values.message') ?? message('values.root.message') ?? message('message');
  const needsValues = axis.values.length === 0;

  const isFresh = (value: OptionValueForm) => {
    if (!markNew) return false;
    const code = optionValueStoredCode(value, savedAxis, attribute);
    return !savedAxis?.values.some((v) => v.value === code);
  };
  const moveValue = (from: number, to: number) => {
    const moved = axis.values[from];
    onChange({ ...axis, values: arrayMove(axis.values, from, to) });
    if (moved) onValueMoved(optionValueLabel(attribute, moved), to + 1, axis.values.length);
  };
  const valueIds = axis.values.map(valueId);
  const nameOfValue = (id: string) => {
    const value = axis.values.find((v) => valueId(v) === id);
    return value ? optionValueLabel(attribute, value) : id;
  };
  const valueError = (at: number): string | undefined => {
    const node = get(errors, `options.${index}.values.${at}`) as unknown;
    return firstMessage(node);
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      aria-label={title}
      className={cn(
        '-mx-4 flex items-start gap-2 border-b px-4 py-3 first:border-t',
        open ? 'bg-muted/40' : 'bg-card',
        isDragging && 'relative z-10 opacity-40',
      )}
    >
      <button
        type="button"
        {...handle}
        aria-label={t('optionsCard.reorder', { name: title })}
        title={t('optionsCard.dragRow')}
        className="text-muted-foreground focus-visible:ring-ring/50 mt-0.5 shrink-0 cursor-grab touch-none rounded outline-none focus-visible:ring-[3px] active:cursor-grabbing"
      >
        <GripVertical className="size-4" aria-hidden />
      </button>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {open ? (
          <>
            <div className="flex flex-col gap-1">
              <Label htmlFor={nameId} className="text-[13px]">
                {t('optionsCard.optionName')}
              </Label>
              <Input
                id={nameId}
                value={axis.name}
                placeholder={attributeLabel}
                maxLength={120}
                className="h-8 text-[13px]"
                aria-invalid={nameError !== undefined}
                onChange={(e) => onChange({ ...axis, name: e.target.value })}
              />
              {nameError ? <p className="text-destructive text-xs">{nameError}</p> : null}
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[13px] font-medium">{t('optionsCard.optionValues')}</span>
              <SortableOptionList ids={valueIds} axis="y" nameOf={nameOfValue} onMove={moveValue}>
                <ul
                  className="flex flex-col"
                  aria-label={t('optionsCard.valuesOf', { option: title })}
                >
                  {axis.values.map((value, at) => {
                    const id = valueId(value);
                    const error = valueError(at);
                    return (
                      <ValueRow
                        key={id}
                        attribute={attribute}
                        value={value}
                        index={at}
                        count={axis.values.length}
                        fresh={isFresh(value)}
                        error={error}
                        panelOpen={panelFor === id || error !== undefined}
                        onTogglePanel={() => setPanelFor(panelFor === id ? null : id)}
                        onChange={(next) =>
                          onChange({ ...axis, values: axis.values.with(at, next) })
                        }
                        onMove={moveValue}
                        onRemove={() =>
                          onChange({ ...axis, values: axis.values.filter((_, i) => i !== at) })
                        }
                      />
                    );
                  })}
                </ul>
              </SortableOptionList>
              {attribute ? (
                <div className="pl-6">
                  <AddValue
                    attribute={attribute}
                    chosen={axis.values}
                    optionName={title}
                    onAdd={(value) => {
                      const again = savedValues?.find((saved) =>
                        value.attributeValueId
                          ? saved.attributeValueId === value.attributeValueId
                          : saved.value === value.value,
                      );
                      onChange({ ...axis, values: [...axis.values, again ?? value] });
                    }}
                  />
                </div>
              ) : null}
              {rowError ? <p className="text-destructive text-xs">{rowError}</p> : null}
            </div>
            <div className="flex items-center justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                aria-label={t('optionsCard.deleteOption', { option: title })}
                onClick={onRemove}
              >
                <X aria-hidden />
                {t('optionsCard.delete')}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  refocusEdit.current = true;
                  onClose();
                }}
              >
                {t('optionsCard.done')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className="min-w-0 truncate font-semibold">{title}</span>
              <span className="text-muted-foreground shrink-0 text-xs">
                {t('optionsCard.source', { count: axis.values.length })}
              </span>
              <span className="flex-1" />
              <Button
                ref={editButton}
                type="button"
                variant="outline"
                size="xs"
                className="shrink-0"
                aria-label={t('optionsCard.editOption', { option: title })}
                onClick={onOpen}
              >
                {t('optionsCard.edit')}
              </Button>
            </div>
            {needsValues ? (
              <p className="text-destructive text-xs">{t('validation.optionNeedsValue')}</p>
            ) : (
              <SortableOptionList ids={valueIds} axis="x" nameOf={nameOfValue} onMove={moveValue}>
                <ul
                  className="flex flex-wrap gap-1.5"
                  aria-label={t('optionsCard.valuesOf', { option: title })}
                >
                  {axis.values.map((value, at) => (
                    <SortableChip
                      key={valueId(value)}
                      attribute={attribute}
                      value={value}
                      index={at}
                      count={axis.values.length}
                      fresh={isFresh(value)}
                      onMove={moveValue}
                    />
                  ))}
                </ul>
              </SortableOptionList>
            )}
          </>
        )}
      </div>
    </li>
  );
}

function firstMessage(node: unknown): string | undefined {
  if (typeof node !== 'object' || node === null) return undefined;
  const { message } = node as { message?: unknown };
  if (typeof message === 'string') return message;
  for (const [key, child] of Object.entries(node)) {
    if (key === 'ref') continue;
    const found = firstMessage(child);
    if (found) return found;
  }
  return undefined;
}
